import { useState } from "react";
import { Button, Modal, Radio, Textarea } from "@wmart/ui";
import { api, ApiError } from "../lib/api-client.js";
import { useToast } from "../lib/toast.js";
import type { SupplierOrderSummaryDTO } from "@wmart/shared-types";

interface Props {
  po: SupplierOrderSummaryDTO;
  onSuccess: () => void | Promise<void>;
  onClose: () => void;
}

const NOTE_MAX = 2000;

/**
 * GRN / goods-in modal. POST /v1/admin/supplier-orders/:id/receive
 * Body: { with_issues: boolean, note?: string }.
 * note is required when with_issues is true (server + UI both enforce it).
 *
 * Mirrors CancelOrderModal structure: react-hook-form free (simple enough
 * with local state), same Modal + Button layout.
 */
export function ReceivePoModal({ po, onSuccess, onClose }: Props) {
  const { notify, notifyProblem } = useToast();
  const [withIssues, setWithIssues] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submitDisabled = busy || (withIssues && note.trim().length === 0);

  async function handleSubmit() {
    setBusy(true);
    try {
      await api.post(`/v1/admin/supplier-orders/${po.id}/receive`, {
        with_issues: withIssues,
        note: withIssues ? note : undefined,
      });
      notify("success", `PO ${po.po_number} received`);
      await onSuccess();
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Failed to mark PO as received");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Receive ${po.po_number}`}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleSubmit()}
            disabled={submitDisabled}
            loading={busy}
          >
            Confirm receipt
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Radio
            name="receive_kind"
            value="ok"
            label="Received OK"
            checked={!withIssues}
            onChange={() => setWithIssues(false)}
          />
          <Radio
            name="receive_kind"
            value="issues"
            label="Received with issues"
            checked={withIssues}
            onChange={() => setWithIssues(true)}
          />
        </div>
        {withIssues ? (
          <Textarea
            label="Issue note"
            rows={3}
            maxLength={NOTE_MAX}
            helper={`${note.length}/${NOTE_MAX} — required`}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        ) : null}
      </div>
    </Modal>
  );
}
